<script setup lang="ts">
import { Order } from '@/@types/order'
import { Button } from '@/components/ui/button'
import { Calendar } from '@/components/ui/calendar'
import { DialogClose, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { formatDateFull } from '@/helpers/format-date'
import { useForm } from '@inertiajs/vue3'
import { getLocalTimeZone, today, type DateValue } from '@internationalized/date'
import { CalendarIcon, Loader2 } from 'lucide-vue-next'
import { ref, watch } from 'vue'

const { order } = defineProps<{ order: Order }>()

const form = useForm({
    status: 'released',
    company_billing_due_date: '',
})

const date = ref<DateValue>()

watch(date, () => {
    form.company_billing_due_date = date.value ? date.value.toString() : ''
})
</script>

<template>
    <DialogContent class="font-inter z-50 p-8 text-gray-800">
        <DialogHeader class="flex gap-2">
            <DialogTitle class="text-2xl">{{ $t('Do you confirm the release of this order?') }}</DialogTitle>
            <DialogDescription class="text-sm font-normal text-gray-800">{{
                $t(
                    'By confirming, the buyer will receive the order confirmation and the tickets will be available for management, even without payment confirmation.',
                )
            }}</DialogDescription>
        </DialogHeader>
        <DialogFooter>
            <form
                @submit.prevent="form.patch(`/admin/orders/${order.id}/update_status`, { preserveState: false })"
                patch
                class="flex w-full flex-col gap-6"
            >
                <div class="flex flex-col gap-1.5">
                    <Label for="due_date" required>{{ $t('Due date') }}</Label>

                    <Popover>
                        <PopoverTrigger as-child>
                            <Button variant="outline" type="button" class="flex justify-between">
                                {{ form.company_billing_due_date ? formatDateFull(form.company_billing_due_date) : $t('Choose a due date') }}
                                <CalendarIcon class="h-6 w-6" />
                            </Button>
                        </PopoverTrigger>
                        <PopoverContent>
                            <Calendar v-model="date" initial-focus :min-value="today(getLocalTimeZone())" />
                        </PopoverContent>
                    </Popover>
                </div>
                <div class="flex justify-between pt-6">
                    <DialogClose>
                        <Button type="button" variant="ghost" class="text-primary hover:text-primary hover:bg-primary/3 min-w-2/12">
                            {{ $t('Back') }}
                        </Button>
                    </DialogClose>
                    <Button :disabled="form.processing || !form.company_billing_due_date" class="min-w-2/12">
                        <template v-if="!form.processing">
                            {{ $t('Release order') }}
                        </template>
                        <template v-if="form.processing">
                            <span>
                                <Loader2 class="animate-spin" />
                                <span class="sr-only"></span>
                            </span>
                        </template>
                    </Button>
                </div>
            </form>
        </DialogFooter>
    </DialogContent>
</template>
